'use client';

import { useAppStore } from '@/stores/app-store';
import { t } from '@/lib/i18n';
import { getAIInsights, siteScore } from '@/lib/ai-engine';
import StatCard from '../StatCard';
import LeafletMap from '../LeafletMap';
import type { PlantMarker } from '../LeafletMap';
import { useEffect, useMemo, useState } from 'react';
import { AIWarning, AIAnomaly } from '@/lib/types';
import ModuleHero from '../ModuleHero';

type EventType = 'alarm' | 'warning' | 'event';

// One row of GET /api/projects/[id]/events — the recorded `usr_log_<mac_id>`
// history, as opposed to the live conditions the rule engine derives below.
interface LogEvent {
  id: string;
  macId: string;
  deviceName: string;
  timestamp: string | null;
  severity: 'critical' | 'warning' | 'info';
  alarmType: string;
  fieldName: string;
  message: string;
  count: number | null;
}

const SEVERITY_TO_TYPE: Record<LogEvent['severity'], EventType> = {
  critical: 'alarm',
  warning: 'warning',
  info: 'event',
};

function eventTimeLabel(ms: number | null, th: boolean): string {
  if (ms == null) return th ? 'ตอนนี้' : 'now';
  if (!isFinite(ms)) return '—';
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return th ? 'เมื่อครู่' : 'just now';
  if (mins < 60) return th ? `${mins} นาทีที่แล้ว` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return th ? `${hrs} ชม.ที่แล้ว` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return th ? `${days} วันที่แล้ว` : `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

export default function OverviewPage() {
  const currentUser = useAppStore((s) => s.currentUser);
  const plants = useAppStore((s) => s.plants);
  const lang = useAppStore((s) => s.lang);
  const switchPlant = useAppStore((s) => s.switchPlant);
  const th = lang === 'th';

  const [eventFilter, setEventFilter] = useState<'all' | EventType>('all');

  const plantIds = currentUser?.plants || [];

  // Recorded event history per plant. Demo mode (no dbUser) has no log tables,
  // so the list there stays on the derived-only behaviour.
  const dbUser = useAppStore((s) => s.dbUser);
  const [logEvents, setLogEvents] = useState<(LogEvent & { plant: string; pid: string })[]>([]);
  const plantIdsKey = plantIds.join(',');

  useEffect(() => {
    if (!dbUser || !plantIdsKey) { setLogEvents([]); return; }
    let cancelled = false;
    const ids = plantIdsKey.split(',');
    Promise.all(
      ids.map(async (pid) => {
        try {
          const res = await fetch(`/api/projects/${pid}/events?limit=25`);
          if (!res.ok) return [];
          const json = await res.json();
          const name = useAppStore.getState().plants[pid]?.name || '';
          return ((json.events || []) as LogEvent[]).map((e) => ({ ...e, plant: name, pid }));
        } catch {
          return [];
        }
      }),
    ).then((lists) => {
      if (cancelled) return;
      setLogEvents(lists.flat());
    });
    return () => { cancelled = true; };
  }, [dbUser, plantIdsKey]);

  const stats = useMemo(() => {
    let totalDevices = 0;
    let online = 0;
    let offline = 0;
    let totalKW = 0;
    let totalDaily = 0;
    let alertCount = 0;

    const allWarnings: (AIWarning & { _plant?: string; _pid?: string })[] = [];
    const allAnomalies: (AIAnomaly & { _plant?: string; _pid?: string })[] = [];
    const healthScores: { pid: string; name: string; score: number }[] = [];
    const allTemps: number[] = [];

    plantIds.forEach((pid) => {
      const plant = plants[pid];
      if (!plant) return;
      const devs = plant.devices || [];
      totalDevices += devs.length;
      devs.forEach((d) => {
        if (d.st === 'online') {
          online++;
          totalKW += d.data?.kw || 0;
          totalDaily += d.data?.daily || 0;
        } else {
          offline++;
        }
        // Offline readings are stale — they never enter a live average.
        const tv = d.st === 'online' ? d.data?.temp : undefined;
        if (typeof tv === 'number' && !isNaN(tv)) allTemps.push(tv);
      });

      const insights = getAIInsights(plant);
      const criticals = insights.warnings.filter((w) => w.sv === 'critical').length;
      const warns = insights.warnings.filter((w) => w.sv === 'warning').length;
      const anomCount = insights.anomalies.length;
      alertCount += criticals + warns;

      insights.warnings.forEach((w) => {
        allWarnings.push({ ...w, _plant: plant.name, _pid: pid });
      });
      insights.anomalies.forEach((a) => {
        allAnomalies.push({ ...a, _plant: plant.name, _pid: pid });
      });

      const score = siteScore(criticals, warns, anomCount);
      healthScores.push({ pid, name: plant.name, score });
    });

    const avgTemp = allTemps.length ? allTemps.reduce((a, b) => a + b, 0) / allTemps.length : null;
    // Solution-agnostic operational health: share of devices currently online.
    const uptimePct = totalDevices > 0 ? Math.round((online / totalDevices) * 100) : 0;
    return { totalDevices, online, offline, totalKW, totalDaily, alertCount, allWarnings, allAnomalies, healthScores, avgTemp, tempCount: allTemps.length, uptimePct };
  }, [plantIds, plants]);

  const plantList = useMemo(() => {
    return plantIds.map((pid) => {
      const plant = plants[pid];
      if (!plant) return null;
      const devs = plant.devices || [];
      const onCount = devs.filter((d) => d.st === 'online').length;
      const offCount = devs.filter((d) => d.st === 'offline').length;
      const kw = devs.filter((d) => d.st === 'online').reduce((s, d) => s + (d.data?.kw || 0), 0);
      const temps = devs.filter((d) => d.st === 'online').map((d) => d.data?.temp).filter((v): v is number => typeof v === 'number' && !isNaN(v));
      const temp = temps.length ? temps.reduce((a, b) => a + b, 0) / temps.length : null;
      const insights = getAIInsights(plant);
      const alertCount = insights.warnings.filter((w) => w.sv === 'critical' || w.sv === 'warning').length;

      /*
       * "Offline" means the SITE is down — every device silent. A site with a
       * few meters offline is still reporting (e.g. Resonac: 107 of 130 online
       * used to read "Offline"); that is a partial outage, shown as amber with
       * the count, like an alert.
       */
      let status: 'green' | 'amber' | 'red' = 'green';
      if (alertCount > 0 || offCount > 0) status = 'amber';
      if (devs.length > 0 && onCount === 0) status = 'red';

      return { pid, plant, onCount, offCount, kw, temp, alertCount, status };
    }).filter(Boolean) as { pid: string; plant: typeof plants[string]; onCount: number; offCount: number; kw: number; temp: number | null; alertCount: number; status: 'green' | 'amber' | 'red' }[];
  }, [plantIds, plants]);

  /*
   * ⚠️ ไซต์ที่ยังไม่ได้ระบุพิกัดต้อง "ไม่ขึ้นบนแผนที่" ไม่ใช่ปักที่ค่าเริ่มต้น
   *
   * ของเดิมใช้ `lat ?? 13.75, lng ?? 100.55` ซึ่งทำให้ไซต์ที่ไม่มีพิกัดทุกอัน
   * ไปกองอยู่กลางกรุงเทพฯ จุดเดียวกันหมด — แผนที่จึงยืนยันว่าโรงพยาบาลที่ภูเก็ต
   * เชียงใหม่ และหาดใหญ่ อยู่ในกรุงเทพฯ ซึ่งผิดและดูไม่ออกว่าผิด
   *
   * แผนที่ที่แสดงน้อยกว่าความจริงยังแก้ได้ด้วยการกรอกพิกัด แต่แผนที่ที่ชี้ผิด
   * ทำให้คนวางแผนเดินทางผิด
   */
  const missingGeo = useMemo(
    () => plantList.filter((i) => i.plant.lat == null || i.plant.lng == null).length,
    [plantList]
  );

  const mapMarkers: PlantMarker[] = useMemo(() => {
    return plantList
      .filter((item) => item.plant.lat != null && item.plant.lng != null)
      .map((item) => {
      const insights = getAIInsights(item.plant);
      const hasWarnings = insights.warnings.some((w) => w.sv === 'critical' || w.sv === 'warning');
      const allOffline = item.plant.devices.length > 0 && item.plant.devices.every((d) => d.st === 'offline');

      let status: 'ok' | 'warning' | 'offline' = 'ok';
      if (hasWarnings) status = 'warning';
      if (allOffline) status = 'offline';

      const inverterKw = item.plant.devices
        .filter((d) => d.type === 'inverter_solar' && d.st === 'online')
        .reduce((sum, d) => sum + (d.data?.kw || 0), 0);

      // Representative fridge temperature (avg of devices reporting `temp`).
      const temps = item.plant.devices
        .filter((d) => d.st === 'online')
        .map((d) => d.data?.temp)
        .filter((v): v is number => typeof v === 'number' && !isNaN(v));
      const temp = temps.length ? temps.reduce((a, b) => a + b, 0) / temps.length : null;

      return {
        pid: item.pid,
        name: item.plant.name,
        addr: item.plant.addr,
        lat: item.plant.lat as number,
        lng: item.plant.lng as number,
        status,
        onCount: item.onCount,
        offCount: item.offCount,
        kw: inverterKw,
        temp,
      };
    });
  }, [plantList]);

  // Unified device event/alert feed: offline devices (Alarm), AI threshold
  // warnings (Alarm/Warning by severity), and AI anomalies (Event).
  // Two sources, deliberately kept distinct:
  //   · live   — conditions true right now, derived from device state + the rule
  //              engine. No timestamp of its own; shown as "now".
  //   · logged — what the devices actually recorded in usr_log_<mac_id>, with
  //              real timestamps. This is the only source of past events; the
  //              derived half can only ever describe the present.
  const deviceEvents = useMemo(() => {
    type Ev = { type: EventType; title: string; desc: string; plant: string; pid: string; time: number | null; live: boolean };
    const evs: Ev[] = [];

    // Newest recorded offline entry per device, so a device that is offline
    // right now can be dated instead of just saying "now".
    const offlineLog = new Map<string, { ms: number; id: string }>();
    logEvents.forEach((e) => {
      if (!/offline/i.test(e.alarmType) || !e.timestamp) return;
      const ms = new Date(e.timestamp).getTime();
      const cur = offlineLog.get(e.macId);
      if (!cur || ms > cur.ms) offlineLog.set(e.macId, { ms, id: e.id });
    });
    const consumed = new Set<string>();
    // Devices this page already reports as offline. The rule engine raises its
    // own offline warning for each of them, so without this the same outage
    // appears twice — once dated from the log, once undated from the engine.
    const offlineDevices = new Set<string>();

    plantIds.forEach((pid) => {
      const plant = plants[pid];
      if (!plant) return;
      (plant.devices || []).forEach((d) => {
        if (d.st !== 'offline') return;
        offlineDevices.add(d.sn);
        const rec = offlineLog.get(d.sn);
        if (rec) consumed.add(rec.id);   // the live row carries this one's time
        evs.push({
          type: 'alarm',
          title: `${d.name} ${th ? 'ออฟไลน์' : 'Offline'}`,
          desc: th ? 'อุปกรณ์ไม่ตอบสนอง — ตรวจสอบการเชื่อมต่อ' : 'Device not responding — check the connection',
          plant: plant.name,
          pid,
          time: rec?.ms ?? null,
          live: true,
        });
      });
    });

    stats.allWarnings.forEach((w) => {
      if (w.device && offlineDevices.has(w.device)) return;   // already listed above, with a time
      evs.push({ type: w.sv === 'critical' ? 'alarm' : 'warning', title: w.title, desc: w.desc, plant: w._plant || '', pid: w._pid || '', time: null, live: true });
    });
    stats.allAnomalies.forEach((a) => {
      evs.push({ type: 'event', title: a.title, desc: a.desc, plant: a._plant || '', pid: a._pid || '', time: null, live: true });
    });

    logEvents.forEach((e) => {
      if (consumed.has(e.id)) return;
      const field = e.fieldName && e.fieldName !== 'Device Status' ? `${e.fieldName} · ` : '';
      evs.push({
        type: SEVERITY_TO_TYPE[e.severity],
        title: `${e.deviceName} — ${e.alarmType || (th ? 'เหตุการณ์' : 'Event')}`,
        desc: `${field}${e.message}`,
        plant: e.plant,
        pid: e.pid,
        time: e.timestamp ? new Date(e.timestamp).getTime() : null,
        live: false,
      });
    });

    // Live-but-undated first (they describe the present), then newest recorded.
    return evs.sort((a, b) => {
      if (a.time == null && b.time == null) return 0;
      if (a.time == null) return -1;
      if (b.time == null) return 1;
      return b.time - a.time;
    });
  }, [plantIds, plants, stats.allWarnings, stats.allAnomalies, logEvents, th]);

  const eventCounts = useMemo(() => ({
    all: deviceEvents.length,
    alarm: deviceEvents.filter((e) => e.type === 'alarm').length,
    warning: deviceEvents.filter((e) => e.type === 'warning').length,
    event: deviceEvents.filter((e) => e.type === 'event').length,
  }), [deviceEvents]);

  const filteredEvents = eventFilter === 'all' ? deviceEvents : deviceEvents.filter((e) => e.type === eventFilter);

  const eventMeta: Record<EventType, { label: string; color: string; bg: string }> = {
    alarm: { label: 'Alarm', color: 'var(--red)', bg: 'var(--red-dim)' },
    warning: { label: 'Warning', color: 'var(--amber)', bg: 'var(--amber-dim)' },
    event: { label: 'Event', color: 'var(--blue)', bg: 'var(--blue-dim)' },
  };

  const statusDotColor = (status: 'green' | 'amber' | 'red') => {
    if (status === 'green') return '#10B77F';
    if (status === 'amber') return '#F5A623';
    return '#DC2626';
  };

  const statusBadge = (status: 'green' | 'amber' | 'red', offCount = 0) => {
    const map: Record<string, { cls: string; label: string }> = {
      green: { cls: 'badge-on', label: 'OK' },
      // some devices down but the site reporting: say how many, not "Offline"
      amber: { cls: 'badge-off', label: offCount > 0 ? (th ? `ออฟไลน์ ${offCount} ตัว` : `${offCount} offline`) : 'Warning' },
      red: { cls: 'badge-off', label: 'Offline' },
    };
    const m = map[status];
    // Warning uses amber styling
    if (status === 'amber') {
      return (
        <span className="badge" style={{ background: 'var(--amber-dim)', color: 'var(--amber)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--amber)', boxShadow: '0 0 6px var(--amber)' }} />
          {m.label}
        </span>
      );
    }
    return (
      <span className={`badge ${m.cls}`}>
        {m.label}
      </span>
    );
  };

  const panelStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    borderRadius: 16,
    border: '1px solid var(--glass-border)',
    padding: 24,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
  };

  return (
    <div style={{ padding: 0 }}>
      <ModuleHero
        eyebrow={th ? 'ภาพรวม · ทุกไซต์' : 'OVERVIEW · ALL SITES'}
        title={th ? 'ภาพรวมการดำเนินงาน' : 'Operation Overview'}
        desc={th ? 'วันนี้มีอะไรต้องให้คุณสนใจบ้าง — รวมทุกไซต์ไว้ที่เดียว' : 'What needs you today — every site in one place'}
        accent="prime"
        icon={
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="9" rx="1.5" />
            <rect x="14" y="3" width="7" height="5" rx="1.5" />
            <rect x="14" y="12" width="7" height="9" rx="1.5" />
            <rect x="3" y="16" width="7" height="5" rx="1.5" />
          </svg>
        }
        stats={[
          { value: stats.totalDevices, label: th ? 'อุปกรณ์' : 'devices' },
          { value: stats.online, label: th ? 'ออนไลน์' : 'online' },
          { value: stats.alertCount, label: th ? 'แจ้งเตือน' : 'alerts' },
        ]}
      />

      {/* Stat cards row — 4-across on desktop, 2x2 on mobile (see .ov-stat-grid) */}
      <div className="ov-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <StatCard
          icon={
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="9" rx="1.5" />
              <rect x="14" y="3" width="7" height="9" rx="1.5" />
              <rect x="3" y="15" width="7" height="6" rx="1.5" />
              <rect x="14" y="15" width="7" height="6" rx="1.5" />
              <path d="M19 1v2M5 1v2" />
              <circle cx="17" cy="6" r="1" fill="currentColor" stroke="none" />
              <path d="M17 6l3-2" />
            </svg>
          }
          label={th ? 'ไซต์' : 'Sites'}
          value={plantIds.length}
          sub={`${stats.totalDevices} ${t('totalDevices', lang)}`}
          gradient="linear-gradient(135deg,#3B7BF7,#2563EB)"
        />
        <StatCard
          icon={
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="4" width="16" height="16" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <circle cx="15.5" cy="8.5" r="1.5" />
              <circle cx="8.5" cy="15.5" r="1.5" />
              <circle cx="15.5" cy="15.5" r="1.5" />
              <path d="M10 8.5h3.5M10 15.5h3.5M8.5 10v3.5M15.5 10v3.5" />
              <path d="M4 12h-2M22 12h-2M12 4V2M12 22v-2" />
            </svg>
          }
          label={t('devices', lang)}
          value={stats.totalDevices}
          sub={`${stats.online} ${t('online', lang)} / ${stats.offline} ${t('offline', lang)}`}
          gradient="linear-gradient(135deg,#10B77F,#059669)"
        />
        <StatCard
          icon={
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          }
          label={th ? 'อัปไทม์' : 'Uptime'}
          value={`${stats.uptimePct}%`}
          sub={th ? `${stats.online}/${stats.totalDevices} อุปกรณ์ออนไลน์` : `${stats.online}/${stats.totalDevices} devices online`}
          gradient="linear-gradient(135deg,#F5A623,#E88D0C)"
        />
        <StatCard
          icon={
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3L2 8.5l10 5.5 10-5.5L12 3z" />
              <path d="M2 8.5v7l10 5.5 10-5.5v-7" />
              <path d="M12 14v3" />
              <circle cx="12" cy="19.5" r="0.5" fill="currentColor" />
            </svg>
          }
          label={t('warnings', lang)}
          value={stats.alertCount}
          sub={stats.alertCount > 0 ? t('needCheck', lang) : t('systemOK', lang)}
          gradient={
            stats.alertCount > 0
              ? 'linear-gradient(135deg,#FF7086,#DC2626)'
              : 'linear-gradient(135deg,#10B77F,#059669)'
          }
        />
      </div>

      {/* Map + Plant list row */}
      <div className="ov-main-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 596px', gap: 16, marginBottom: 20, alignItems: 'stretch', height: 780 }}>
        {/* Map panel */}
        <div style={{ ...panelStyle, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a7 7 0 0 0-7 7c0 3 2.5 6 7 11 4.5-5 7-8 7-11a7 7 0 0 0-7-7z" /><circle cx="12" cy="9" r="2.5" />
              </svg>
              Live Map
              {/* บอกตรง ๆ ว่ามีไซต์ที่ยังไม่ขึ้นแผนที่กี่อัน ไม่งั้นคนจะนับหมุดแล้ว
                  นึกว่าไซต์หายไปไหน หรือแย่กว่านั้นคือไม่รู้ว่าขาด */}
              {missingGeo > 0 && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                  color: 'var(--amber)', background: 'color-mix(in srgb, var(--amber) 15%, transparent)',
                  letterSpacing: 0,
                }}>
                  {lang === 'th'
                    ? `${missingGeo} ไซต์ยังไม่ได้ระบุพิกัด`
                    : `${missingGeo} sites have no coordinates`}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--green)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 6px var(--green)' }} />OK
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--amber)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)', boxShadow: '0 0 6px var(--amber)' }} />Alert
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--red)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--red)', boxShadow: '0 0 6px var(--red)' }} />Offline
              </span>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>
            <LeafletMap plants={mapMarkers} onEnterPlant={switchPlant} height="100%" />
          </div>
        </div>

        {/* Right column: plants + events */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
        {/* Plant list panel */}
        <div style={{ ...panelStyle, padding: 0, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '16px 24px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="8" width="8" height="14" rx="1.5" /><rect x="14" y="3" width="8" height="19" rx="1.5" /><line x1="4.5" y1="11" x2="7.5" y2="11" /><line x1="16.5" y1="6" x2="19.5" y2="6" /><line x1="16.5" y1="9" x2="19.5" y2="9" /></svg>
              {t('allPlants', lang)}
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--gold-dim)', color: 'var(--gold)' }}>{plantList.length}</span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {plantList.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No plants
              </div>
            )}
            {plantList.map((item, idx) => {
              const dotColor = statusDotColor(item.status);
              return (
                <div
                  key={item.pid}
                  onClick={() => switchPlant(item.pid)}
                  style={{
                    padding: '14px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                    borderBottom: idx < plantList.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                    transition: 'all .25s cubic-bezier(.16,1,.3,1)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; e.currentTarget.style.paddingLeft = '28px'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.paddingLeft = '24px'; }}
                >
                  {/* Status dot with glow */}
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, boxShadow: `0 0 8px ${dotColor}`, flexShrink: 0 }} />

                  {/* Plant info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.01em' }}>
                      {item.plant.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
                        {item.onCount}/{item.onCount + item.offCount} Online
                      </span>
                      <span style={{ color: 'var(--text-muted)' }}>&middot;</span>
                      <span>{item.temp != null ? `${item.temp.toFixed(1)}°C` : '—'}</span>
                    </div>
                  </div>

                  {/* Status badge */}
                  {statusBadge(item.status, item.offCount)}

                  {/* Arrow */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.5 }}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              );
            })}
          </div>
        </div>

        {/* Device events & alerts */}
        <div style={{ ...panelStyle, padding: 0, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-subtle)', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            {th ? 'เหตุการณ์และการแจ้งเตือนอุปกรณ์' : 'Device Events & Alerts'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {([['all', th ? 'ทั้งหมด' : 'All'], ['alarm', 'Alarm'], ['warning', 'Warning'], ['event', 'Event']] as const).map(([key, label]) => {
              const active = eventFilter === key;
              const color = key === 'all' ? 'var(--text-primary)' : eventMeta[key].color;
              const bg = key === 'all' ? 'var(--bg-elevated)' : eventMeta[key].bg;
              return (
                <button
                  key={key}
                  onClick={() => setEventFilter(key)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 99,
                    fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: `1px solid ${active ? color : 'var(--border-subtle)'}`,
                    background: active ? bg : 'transparent',
                    color: active ? color : 'var(--text-muted)',
                  }}
                >
                  {label}
                  <span style={{ fontSize: 10, opacity: 0.85 }}>{eventCounts[key]}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {filteredEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="var(--green)" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, marginBottom: 8 }}>
                <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" />
              </svg>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{th ? 'ไม่มีเหตุการณ์' : 'No events'}</div>
            </div>
          ) : filteredEvents.map((ev, idx) => {
            const m = eventMeta[ev.type];
            return (
              <div
                key={idx}
                onClick={() => ev.pid && switchPlant(ev.pid)}
                style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 20px',
                  borderBottom: idx < filteredEvents.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  cursor: ev.pid ? 'pointer' : 'default', transition: 'background .2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 6, background: m.bg, color: m.color, flexShrink: 0, marginTop: 2, minWidth: 56, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {m.label}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginLeft: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {eventTimeLabel(ev.time, th)}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{ev.desc}</div>
                  {ev.plant && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="8" width="8" height="14" rx="1" /><rect x="13" y="3" width="8" height="19" rx="1" /></svg>
                      {ev.plant}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
        </div>
      </div>

    </div>
  );
}
